import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import logoWithText from "../assets/images/headerlogo.svg";
import solveMiniView from "../assets/images/cash_mini_view.PNG";
import trainMiniView from "../assets/images/train_mini_view.PNG";
import playMiniView from "../assets/images/play_mini_view.PNG";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";

const COLORS = {
  bg: "#101012",
  cardBg: "#222222",
  panelBg: "#1f1f1f",
  border: "#333333",
  text: "#e0e0e0",
  textMuted: "#8f8f8f",
  white: "#ffffff",
  green: "#56ECCF",
  gold: "#B99E4F",
  black: "#282828",
  danger: "#e57f7f",
};
const SUIT_COLORS = { s: "#888888", h: "#c44", d: "#48f", c: "#4a4" };
const SUIT_SYMBOLS = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };

const RECENT_SOLVES_KEY = "pm_recent_solves_v1";
const TRAINER_STATS_KEY = "pm_trainer_stats_v1";

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed == null ? fallback : parsed;
  } catch (_) {
    return fallback;
  }
}

function timeAgo(iso) {
  const ts = new Date(iso || 0).getTime();
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

function formatChips(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return `${Math.round(v)}`;
}

function displayNameFromEmail(email) {
  const namePart = String(email || "").split("@")[0] || "";
  if (!namePart) return "";
  const cleaned = namePart.replace(/[._-]+/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function stakesForAccuracy(pct) {
  if (pct == null || !Number.isFinite(pct)) {
    return { online: "-", live: "-", label: "Train to unlock a stakes recommendation", pathId: null };
  }
  if (pct >= 100) return { online: "500NL", live: "$2500+", label: "Cash pro territory", pathId: "500NL" };
  if (pct >= 90) return { online: "200NL", live: "$1000", label: "High stakes ready", pathId: "200NL" };
  if (pct >= 85) return { online: "100NL", live: "$500", label: "Strong mid stakes", pathId: "100NL" };
  if (pct >= 80) return { online: "50NL", live: "$200", label: "Solid small/mid", pathId: "50NL" };
  if (pct >= 70) return { online: "25NL", live: "$100", label: "Building edge", pathId: "25NL" };
  return { online: "10NL", live: "$50", label: "Focus on fundamentals", pathId: "10NL" };
}

const PRO_PATH = [
  { id: "10NL", label: "10NL", sub: "Fundamentals" },
  { id: "25NL", label: "25NL", sub: "Building edge" },
  { id: "50NL", label: "50NL", sub: "Small / mid" },
  { id: "100NL", label: "100NL", sub: "Strong mid" },
  { id: "200NL", label: "200NL", sub: "High stakes" },
  { id: "500NL", label: "500NL+", sub: "Cash pro" },
  { id: "pro", label: "Pro", sub: "Professional Poker" },
];

const STAKE_RANK = {
  "10NL": 0,
  "25NL": 1,
  "50NL": 2,
  "100NL": 3,
  "200NL": 4,
  "500NL": 5,
  "500NL+": 5,
};

function parseOnboardingOnlineStakes(stakesText) {
  const text = String(stakesText || "");
  const onlineMatch = text.match(/Online:\s*([^|]+)/i);
  if (!onlineMatch) return [];
  return onlineMatch[1]
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function pathIndexForStakeLabel(label) {
  const key = String(label || "").trim().toUpperCase().replace(/\s+/g, "");
  if (key === "PRO" || key === "PROFESSIONAL") return PRO_PATH.length - 1;
  if (Object.prototype.hasOwnProperty.call(STAKE_RANK, key)) return STAKE_RANK[key];
  if (key.endsWith("NL+")) {
    const n = Number(key.replace(/NL\+$/, ""));
    if (n >= 500) return STAKE_RANK["500NL"];
  }
  return null;
}

function buildProPathProgress(stakeRec, onboarding) {
  const why = String(onboarding?.whySignup || "").toLowerCase();
  const expectations = Array.isArray(onboarding?.expectations) ? onboarding.expectations : [];
  const wantsPro = why === "become_pro";
  const wantsHigher = expectations.includes("beat_stakes") || why === "money_on_side";

  const played = parseOnboardingOnlineStakes(onboarding?.stakesText);
  let goalFromStakes = null;
  for (const s of played) {
    const idx = pathIndexForStakeLabel(s);
    if (idx != null && (goalFromStakes == null || idx > goalFromStakes)) goalFromStakes = idx;
  }

  let currentIdx = pathIndexForStakeLabel(stakeRec?.pathId || stakeRec?.online);
  if (currentIdx == null && played.length) {
    // No trainer data yet: place them at their lowest stated stake.
    let minIdx = null;
    for (const s of played) {
      const idx = pathIndexForStakeLabel(s);
      if (idx != null && (minIdx == null || idx < minIdx)) minIdx = idx;
    }
    currentIdx = minIdx;
  }
  if (currentIdx == null) currentIdx = 0;

  let goalIdx = PRO_PATH.length - 1;
  if (wantsPro) {
    goalIdx = PRO_PATH.length - 1;
  } else if (goalFromStakes != null) {
    goalIdx = Math.max(goalFromStakes, currentIdx);
    if (wantsHigher) goalIdx = Math.min(PRO_PATH.length - 1, Math.max(goalIdx, currentIdx + 1));
  } else if (wantsHigher) {
    goalIdx = Math.min(PRO_PATH.length - 1, currentIdx + 2);
  }

  if (goalIdx < currentIdx) goalIdx = currentIdx;

  const current = PRO_PATH[currentIdx];
  const goal = PRO_PATH[goalIdx];
  const progressPct =
    PRO_PATH.length <= 1 ? 100 : Math.round((currentIdx / (PRO_PATH.length - 1)) * 100);

  let goalReason = "Professional Poker";
  if (wantsPro) goalReason = "You chose Become a pro";
  else if (goalFromStakes != null) goalReason = `From your stakes: ${played.join(", ")}`;
  else if (wantsHigher) goalReason = "You want to beat higher stakes";

  return {
    steps: PRO_PATH,
    currentIdx,
    goalIdx,
    current,
    goal,
    progressPct,
    goalReason,
    youAreHere: current?.label || "10NL",
    destination: goal?.id === "pro" ? "Professional Poker" : goal?.label,
  };
}

const MiniCard = ({ card }) => (
  <Box
    sx={{
      width: 26,
      height: 36,
      backgroundColor: COLORS.black,
      border: `1px solid ${SUIT_COLORS[card?.[1]] || COLORS.border}`,
      borderRadius: "4px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SUIT_COLORS[card?.[1]] || COLORS.textMuted, lineHeight: 1 }}>
      {card?.[0] || "?"}
    </Typography>
    <Typography sx={{ fontSize: 9, color: SUIT_COLORS[card?.[1]] || COLORS.textMuted, lineHeight: 1 }}>
      {SUIT_SYMBOLS[card?.[1]] || ""}
    </Typography>
  </Box>
);

const StatPill = ({ label, value, color }) => (
  <Box sx={{ backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "10px", px: 1.4, py: 0.9, flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 800, color: color || COLORS.white, lineHeight: 1.25, whiteSpace: "nowrap" }}>{value}</Typography>
  </Box>
);

const MiniView = ({ src, alt }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: { xs: 200, sm: 260 },
      height: { xs: 130, sm: 160 },
      objectFit: "contain",
      borderRadius: "14px",
      border: `1px solid ${COLORS.border}`,
      flexShrink: 0,
      display: "block",
      backgroundColor: "#151515",
      p: 0.5,
    }}
  />
);

const FeatureRow = ({
  title,
  body,
  accent,
  action,
  onActionClick,
  visual,
  children,
}) => (
  <Box
    onClick={onActionClick}
    sx={{
      width: "100%",
      backgroundColor: COLORS.cardBg,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "14px",
      p: { xs: 1.5, sm: 2 },
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.05fr) minmax(0, 1fr)" },
      gap: { xs: 1.4, md: 2.2 },
      alignItems: "stretch",
      cursor: "pointer",
      transition: "border-color 160ms ease, background-color 160ms ease",
      "&:hover": {
        borderColor: accent || COLORS.green,
        backgroundColor: "#242424",
      },
    }}
  >
    <Box sx={{ display: "flex", gap: { xs: 1.2, sm: 1.6 }, alignItems: "flex-start", minWidth: 0 }}>
      {visual}
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.7 }}>
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 800,
            color: COLORS.white,
            fontFamily: "Play, Arial, sans-serif",
            letterSpacing: "0.01em",
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: COLORS.text, lineHeight: 1.45, maxWidth: 420 }}>
          {body}
        </Typography>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onActionClick?.();
          }}
          sx={{
            mt: 0.4,
            alignSelf: "flex-start",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            color: "#111",
            backgroundColor: accent || COLORS.green,
            borderRadius: "999px",
            px: 1.6,
            minHeight: 30,
            "&:hover": {
              backgroundColor: "rgb(45, 179, 155)",
            },
          }}
        >
          {action}
        </Button>
      </Box>
    </Box>
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        minWidth: 0,
        backgroundColor: COLORS.panelBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: "12px",
        p: { xs: 1.2, sm: 1.4 },
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {children}
    </Box>
  </Box>
);

export default function Home() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [accountName, setAccountName] = React.useState("");
  const [onboarding, setOnboarding] = React.useState(null);
  const [playData, setPlayData] = React.useState(null);

  const recentSolves = React.useMemo(() => {
    const list = readJson(RECENT_SOLVES_KEY, []);
    return Array.isArray(list) ? list.slice(0, 2) : [];
  }, []);
  const trainerStats = React.useMemo(() => readJson(TRAINER_STATS_KEY, null), []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/auth/session`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data?.email) setEmail(String(data.email));
        if (!cancelled && res.ok && data?.name) setAccountName(String(data.name));
        if (!cancelled && res.ok && data?.onboarding) setOnboarding(data.onboarding);
      } catch (_) {}
    })();
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/auth/play/profile`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data?.success) setPlayData(data);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefer the name the user entered at signup; fall back to the email prefix.
  const firstName = (String(accountName || "").trim().split(/\s+/)[0]) || displayNameFromEmail(email);
  const trainerTotal = Number(trainerStats?.totalDecisions || 0);
  const trainerCorrect = Number(trainerStats?.correct || 0);
  const trainerAccuracy = trainerTotal > 0 ? Math.round((trainerCorrect / trainerTotal) * 100) : null;
  const stakeRec = stakesForAccuracy(trainerAccuracy);
  const proPath = React.useMemo(() => buildProPathProgress(stakeRec, onboarding), [stakeRec, onboarding]);
  const bankroll = Number(playData?.bankroll || 0);
  const overallScore = Number(playData?.profile?.overallScore || 0);
  const playSessions = Array.isArray(playData?.profile?.sessions) ? playData.profile.sessions.slice(0, 3) : [];

  const navTabSx = (active) => ({
    minWidth: "unset",
    px: { xs: 1.6, sm: 2.1 },
    py: { xs: 0.45, sm: 0.6 },
    borderRadius: "999px",
    border: "none",
    backgroundColor: active ? "rgba(185,158,79,0.14)" : "rgba(255,255,255,0.02)",
    color: active ? COLORS.white : COLORS.textMuted,
    fontSize: { xs: "11px", sm: "12px" },
    fontWeight: active ? 700 : 600,
    textTransform: "none",
    letterSpacing: "0.01em",
    lineHeight: 1.2,
    transition: "all 160ms ease",
    "&:hover": {
      color: COLORS.white,
      backgroundColor: active ? "rgba(185,158,79,0.22)" : "rgba(255,255,255,0.07)",
    },
  });

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: COLORS.bg, color: COLORS.text }}>
      {/* Header; matches Trainer/Play pattern */}
      <Box sx={{ py: 1.5, px: 2, pl: { xs: 1.2, sm: 3 }, pr: { xs: 14, sm: 2 }, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0, gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.6, sm: 1.2 }, minWidth: 0, flex: 1, overflowX: "auto" }}>
          <img src={logoWithText} alt="PokerMoose" onClick={() => navigate("/")} style={{ height: 32, width: 32, flexShrink: 0, opacity: 0.5, filter: "grayscale(1)", cursor: "pointer" }} />
          <Typography
            sx={{
              display: { xs: "none", sm: "inline" },
              color: COLORS.white,
              fontSize: "13px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontFamily: "Play, Arial, sans-serif",
              lineHeight: 1,
              mr: 0.4,
              userSelect: "none",
              flexShrink: 0,
            }}
          >
            PokerMoose
          </Typography>
          <Button variant="text" size="small" onClick={() => navigate("/")} sx={{ ...navTabSx(true), flexShrink: 0 }}>Home</Button>
          <Button variant="text" size="small" onClick={() => navigate("/cash")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Solve</Button>
          <Button variant="text" size="small" onClick={() => navigate("/trainer")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Train</Button>
          <Button variant="text" size="small" onClick={() => navigate("/play")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Play</Button>
          <Button variant="text" size="small" onClick={() => navigate("/analysis")} sx={{ ...navTabSx(false), flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}>Analyze</Button>
        </Box>
        <Box />
      </Box>

      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 1.4, sm: 2.5 }, py: { xs: 2, sm: 3 } }}>
        {/* Greeting */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800, color: COLORS.white, lineHeight: 1.2, fontFamily: "Play, Arial, sans-serif" }}>
            {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
          </Typography>

          <Box
            sx={{
              mt: 1.4,
              width: "100%",
              backgroundColor: COLORS.panelBg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              px: { xs: 1.4, sm: 1.8 },
              py: { xs: 1.3, sm: 1.5 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1.2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: COLORS.white }}>
                Path to Professional Poker
              </Typography>
              <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>
                You: <Box component="span" sx={{ color: COLORS.green, fontWeight: 700 }}>{proPath.youAreHere}</Box>
                {" / "}
                Goal: <Box component="span" sx={{ color: COLORS.white, fontWeight: 700 }}>{proPath.destination}</Box>
              </Typography>
            </Box>

            <Box sx={{ position: "relative", pt: 0.6, pb: 0.4 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: "4%",
                  right: "4%",
                  top: 18,
                  height: 3,
                  borderRadius: 99,
                  backgroundColor: "#2a2a2a",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: "4%",
                  top: 18,
                  height: 3,
                  borderRadius: 99,
                  width: `${Math.max(0, Math.min(100, proPath.progressPct)) * 0.92}%`,
                  backgroundColor: COLORS.green,
                  transition: "width 240ms ease",
                }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${proPath.steps.length}, 1fr)`,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {proPath.steps.map((step, idx) => {
                  const reached = idx <= proPath.currentIdx;
                  const isCurrent = idx === proPath.currentIdx;
                  const isGoal = idx === proPath.goalIdx;
                  return (
                    <Box key={step.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 0.2 }}>
                      <Box
                        sx={{
                          width: isCurrent || isGoal ? 16 : 12,
                          height: isCurrent || isGoal ? 16 : 12,
                          borderRadius: "50%",
                          backgroundColor: reached ? COLORS.green : "#3a3a3a",
                          border: (isGoal || isCurrent)
                            ? `2px solid ${COLORS.white}`
                            : "2px solid transparent",
                          boxShadow: isCurrent ? `0 0 0 3px rgba(86,236,207,0.2)` : "none",
                          mb: 0.7,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: 9, sm: 11 },
                          fontWeight: isCurrent || isGoal ? 800 : 600,
                          color: isGoal || reached ? COLORS.white : COLORS.textMuted,
                          textAlign: "center",
                          lineHeight: 1.15,
                        }}
                      >
                        {step.label}
                      </Typography>
                      <Typography
                        sx={{
                          display: { xs: "none", sm: "block" },
                          fontSize: 9.5,
                          color: COLORS.textMuted,
                          textAlign: "center",
                          mt: 0.2,
                          lineHeight: 1.2,
                        }}
                      >
                        {isCurrent ? "You are here" : isGoal ? "Your goal" : step.sub}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 1.1, lineHeight: 1.45 }}>
              Recommended stakes set where you are now
              {trainerAccuracy != null ? ` (${trainerAccuracy}% trainer accuracy)` : ""}.
              {" "}
              {proPath.goalReason}.
            </Typography>
          </Box>
        </Box>

        {/* Product rows: Solve / Train / Play */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.4, sm: 1.8 } }}>
          <FeatureRow
            title="Solve"
            body="Get the perfect mathematical move for any situation"
            accent={COLORS.green}
            action="Open Solver"
            onActionClick={() => navigate("/cash")}
            visual={<MiniView src={solveMiniView} alt="Solve" />}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Recent solves
            </Typography>
            {recentSolves.length === 0 ? (
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted }}>No solves yet. Open a spot to get started.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                {recentSolves.map((s) => (
                  <Box
                    key={s.id}
                    onClick={() => navigate("/cash")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      backgroundColor: COLORS.cardBg,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "10px",
                      px: 1.2,
                      py: 0.8,
                      cursor: "pointer",
                      transition: "border-color 140ms ease",
                      "&:hover": { borderColor: COLORS.gold },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {(s.flop || []).map((c, i) => (
                        <MiniCard key={i} card={c} />
                      ))}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {s.spotName || "Solve"}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted }}>{timeAgo(s.at)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </FeatureRow>

          <FeatureRow
            title="Train"
            body="Learn through playing the optimal lines"
            accent={COLORS.green}
            action="Train now"
            onActionClick={() => navigate("/trainer")}
            visual={<MiniView src={trainMiniView} alt="Train" />}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Your training
            </Typography>
            {trainerTotal === 0 ? (
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted }}>Play your first hand to unlock accuracy stats.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <StatPill label="Accuracy" value={`${trainerAccuracy}%`} color={trainerAccuracy >= 70 ? COLORS.green : trainerAccuracy >= 45 ? COLORS.gold : COLORS.danger} />
                  <StatPill label="Decisions" value={trainerTotal} />
                  <StatPill label="Blunders" value={Number(trainerStats?.blunders || 0)} color={COLORS.danger} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                  {["preflop", "flop", "turn", "river"].map((st) => {
                    const row = trainerStats?.byStreet?.[st] || { correct: 0, blunders: 0 };
                    const tot = Number(row.correct || 0) + Number(row.blunders || 0);
                    const pct = tot > 0 ? Math.round((Number(row.correct || 0) / tot) * 100) : 0;
                    return (
                      <Box key={st} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, width: 52, textTransform: "capitalize" }}>{st}</Typography>
                        <Box sx={{ flex: 1, height: 6, backgroundColor: "#2a2a2a", borderRadius: 999, overflow: "hidden" }}>
                          <Box sx={{ width: `${tot > 0 ? pct : 0}%`, height: "100%", backgroundColor: COLORS.green, borderRadius: 999, transition: "width 300ms ease" }} />
                        </Box>
                        <Typography sx={{ fontSize: 10.5, color: tot > 0 ? COLORS.text : COLORS.textMuted, width: 38, textAlign: "right" }}>
                          {tot > 0 ? `${pct}%` : "-"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {trainerStats?.lastSessionAt && (
                  <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted, mt: "auto" }}>
                    Last trained {timeAgo(trainerStats.lastSessionAt)}
                  </Typography>
                )}
              </>
            )}
          </FeatureRow>

          <FeatureRow
            title="Play"
            body="Take on increasing levels of difficulty practicing against the bots"
            accent={COLORS.green}
            action="Play now"
            onActionClick={() => navigate("/play")}
            visual={<MiniView src={playMiniView} alt="Play" />}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Play chips
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <StatPill label="Bankroll" value={`${formatChips(bankroll)} chips`} color={COLORS.green} />
              <StatPill
                label="Overall score"
                value={`${overallScore >= 0 ? "+" : ""}${formatChips(overallScore)}`}
                color={overallScore >= 0 ? COLORS.green : COLORS.danger}
              />
            </Box>
            {playSessions.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                <Typography sx={{ fontSize: 10.5, color: COLORS.textMuted }}>Recent sessions</Typography>
                {playSessions.map((sess) => (
                  <Box key={sess.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: "8px", px: 1.2, py: 0.6 }}>
                    <Typography sx={{ fontSize: 11.5, color: COLORS.text }}>
                      vs {sess.opponentName || sess.opponentId}, {sess.hands} hands
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: Number(sess.score) >= 0 ? COLORS.green : COLORS.danger }}>
                      {Number(sess.score) >= 0 ? "+" : ""}{Number(sess.score).toFixed(0)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: COLORS.textMuted, py: 0.5 }}>
                Challenge a bot to start building your bankroll.
              </Typography>
            )}
          </FeatureRow>
        </Box>
      </Box>
    </Box>
  );
}